import type { AlterlyEffectInstance } from "../../core/effects/effectRegistry";
import { AppError } from "../../shared/errors/AppError";
import { WebGL2Renderer } from "./WebGL2Renderer";
import { createProgram, uniformLocation } from "./shaderProgram";

const VERTEX_SHADER = `#version 300 es
in vec2 a_position;
out vec2 v_uv;
void main() {
  v_uv = a_position * 0.5 + 0.5;
  gl_Position = vec4(a_position, 0.0, 1.0);
}`;

const PASSTHROUGH_SHADER = `#version 300 es
precision highp float;
uniform sampler2D u_source;
in vec2 v_uv;
out vec4 outColor;
void main() {
  outColor = texture(u_source, v_uv);
}`;

const HALFTONE_SHADER = `#version 300 es
precision highp float;
uniform sampler2D u_source;
uniform vec2 u_resolution;
uniform float u_cellSize;
uniform float u_angle;
uniform float u_dotGain;
uniform float u_threshold;
uniform float u_invert;
uniform vec3 u_foreground;
uniform vec3 u_background;
uniform float u_opacity;
in vec2 v_uv;
out vec4 outColor;

vec2 rotate2d(vec2 point, float angle) {
  float c = cos(angle);
  float s = sin(angle);
  return mat2(c, -s, s, c) * point;
}

void main() {
  vec4 sourceColor = texture(u_source, v_uv);
  vec2 center = u_resolution * 0.5;
  vec2 pixel = v_uv * u_resolution;
  float radians = radians(u_angle);
  vec2 gridPoint = rotate2d(pixel - center, -radians);
  vec2 cellCenter = (floor(gridPoint / u_cellSize) + 0.5) * u_cellSize;
  vec2 samplePixel = center + rotate2d(cellCenter, radians);
  vec2 sampleUv = clamp(samplePixel / u_resolution, vec2(0.0), vec2(1.0));
  vec3 sampled = texture(u_source, sampleUv).rgb;
  float luminance = dot(sampled, vec3(0.2126, 0.7152, 0.0722));
  luminance = clamp(luminance + u_threshold, 0.0, 1.0);
  float amount = mix(1.0 - luminance, luminance, u_invert);
  float radius = clamp(amount * (u_cellSize * 0.5) * u_dotGain, 0.0, u_cellSize * 0.75);
  float distanceToCenter = length(gridPoint - cellCenter);
  float edge = max(0.75, fwidth(distanceToCenter));
  float mark = 1.0 - smoothstep(max(0.0, radius - edge), radius + edge, distanceToCenter);
  vec3 effectColor = mix(u_background, u_foreground, mark);
  outColor = vec4(mix(sourceColor.rgb, effectColor, u_opacity), sourceColor.a);
}`;

const NOISE_SHADER = `#version 300 es
precision highp float;
uniform sampler2D u_source;
uniform vec2 u_resolution;
uniform float u_amount;
uniform float u_seed;
uniform float u_monochrome;
uniform float u_opacity;
in vec2 v_uv;
out vec4 outColor;

float hash(vec2 point, float seed) {
  return fract(sin(dot(point + seed, vec2(12.9898, 78.233))) * 43758.5453123);
}

void main() {
  vec4 sourceColor = texture(u_source, v_uv);
  vec2 pixel = floor(v_uv * u_resolution);
  float mono = hash(pixel, u_seed);
  vec3 randomColor = vec3(
    hash(pixel, u_seed + 17.0),
    hash(pixel, u_seed + 43.0),
    hash(pixel, u_seed + 89.0)
  );
  vec3 randomValue = mix(randomColor, vec3(mono), u_monochrome);
  vec3 noisy = clamp(sourceColor.rgb + (randomValue - 0.5) * 2.0 * u_amount, 0.0, 1.0);
  outColor = vec4(mix(sourceColor.rgb, noisy, u_opacity), sourceColor.a);
}`;

type Programs = {
  passthrough: WebGLProgram;
  halftone: WebGLProgram;
  noise: WebGLProgram;
};

function parseHexColor(value: string): [number, number, number] {
  const normalized = value.trim().replace(/^#/, "");
  const expanded = normalized.length === 3
    ? normalized.split("").map((character) => character + character).join("")
    : normalized;
  const parsed = Number.parseInt(expanded.slice(0, 6), 16);
  if (!Number.isFinite(parsed)) return [1, 1, 1];
  return [
    ((parsed >> 16) & 255) / 255,
    ((parsed >> 8) & 255) / 255,
    (parsed & 255) / 255,
  ];
}

function configureTexture(gl: WebGL2RenderingContext, texture: WebGLTexture) {
  gl.bindTexture(gl.TEXTURE_2D, texture);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
}

export class GpuEffectStackRenderer {
  private readonly renderer: WebGL2Renderer;
  private readonly gl: WebGL2RenderingContext;
  private readonly programs: Programs;
  private readonly vao: WebGLVertexArrayObject;
  private readonly vertexBuffer: WebGLBuffer;
  private readonly sourceTexture: WebGLTexture;
  private readonly pingTextures: [WebGLTexture, WebGLTexture];
  private readonly framebuffer: WebGLFramebuffer;
  private width = 0;
  private height = 0;

  constructor(canvas: HTMLCanvasElement) {
    this.renderer = new WebGL2Renderer(canvas, { alpha: true, antialias: false });
    this.gl = this.renderer.gl;
    const gl = this.gl;

    this.programs = {
      passthrough: createProgram(gl, VERTEX_SHADER, PASSTHROUGH_SHADER),
      halftone: createProgram(gl, VERTEX_SHADER, HALFTONE_SHADER),
      noise: createProgram(gl, VERTEX_SHADER, NOISE_SHADER),
    };

    const vao = gl.createVertexArray();
    const buffer = gl.createBuffer();
    const sourceTexture = gl.createTexture();
    const pingA = gl.createTexture();
    const pingB = gl.createTexture();
    const framebuffer = gl.createFramebuffer();
    if (!vao || !buffer || !sourceTexture || !pingA || !pingB || !framebuffer) {
      throw new AppError("GPU_UNAVAILABLE", "Unable to allocate WebGL effect resources.");
    }

    this.vao = vao;
    this.vertexBuffer = buffer;
    this.sourceTexture = sourceTexture;
    this.pingTextures = [pingA, pingB];
    this.framebuffer = framebuffer;

    gl.bindVertexArray(vao);
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([-1, -1, 3, -1, -1, 3]),
      gl.STATIC_DRAW,
    );
    for (const program of Object.values(this.programs)) {
      const position = gl.getAttribLocation(program, "a_position");
      if (position < 0) throw new AppError("GPU_UNAVAILABLE", "GPU shader is missing its position attribute.");
      gl.enableVertexAttribArray(position);
      gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);
    }

    configureTexture(gl, sourceTexture);
    configureTexture(gl, pingA);
    configureTexture(gl, pingB);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
  }

  resize(width: number, height: number) {
    const nextWidth = Math.max(1, Math.floor(width));
    const nextHeight = Math.max(1, Math.floor(height));
    if (this.width === nextWidth && this.height === nextHeight) return;

    this.width = nextWidth;
    this.height = nextHeight;
    this.renderer.resize(nextWidth, nextHeight);

    for (const texture of this.pingTextures) {
      this.gl.bindTexture(this.gl.TEXTURE_2D, texture);
      this.gl.texImage2D(
        this.gl.TEXTURE_2D,
        0,
        this.gl.RGBA,
        nextWidth,
        nextHeight,
        0,
        this.gl.RGBA,
        this.gl.UNSIGNED_BYTE,
        null,
      );
    }
  }

  render(source: CanvasImageSource, effects: readonly AlterlyEffectInstance[]) {
    if (this.renderer.isContextLost()) return false;
    const gl = this.gl;
    const activeEffects = effects.filter((effect) => effect.enabled && effect.opacity > 0);

    gl.bindTexture(gl.TEXTURE_2D, this.sourceTexture);
    try {
      gl.texImage2D(
        gl.TEXTURE_2D,
        0,
        gl.RGBA,
        gl.RGBA,
        gl.UNSIGNED_BYTE,
        source as TexImageSource,
      );
    } catch {
      return false;
    }

    gl.bindVertexArray(this.vao);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.vertexBuffer);
    gl.disable(gl.BLEND);

    if (activeEffects.length === 0) {
      this.drawPass(this.programs.passthrough, this.sourceTexture, null);
      return true;
    }

    let input = this.sourceTexture;
    let pingIndex = 0;
    activeEffects.forEach((effect, index) => {
      const isLast = index === activeEffects.length - 1;
      const output: WebGLTexture | null = isLast ? null : (this.pingTextures[pingIndex] ?? null);
      const program = effect.definitionId === "halftone" ? this.programs.halftone : this.programs.noise;
      this.drawPass(program, input, output, effect);
      if (output) {
        input = output;
        pingIndex = pingIndex === 0 ? 1 : 0;
      }
    });

    return true;
  }

  private drawPass(
    program: WebGLProgram,
    input: WebGLTexture,
    output: WebGLTexture | null,
    effect?: AlterlyEffectInstance,
  ) {
    const gl = this.gl;
    if (output) {
      gl.bindFramebuffer(gl.FRAMEBUFFER, this.framebuffer);
      gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, output, 0);
    } else {
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    }

    gl.viewport(0, 0, this.width, this.height);
    gl.useProgram(program);
    const position = gl.getAttribLocation(program, "a_position");
    gl.bindBuffer(gl.ARRAY_BUFFER, this.vertexBuffer);
    gl.enableVertexAttribArray(position);
    gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, input);
    gl.uniform1i(uniformLocation(gl, program, "u_source"), 0);

    if (effect?.definitionId === "halftone") {
      const [fr, fg, fb] = parseHexColor(effect.parameters.foreground);
      const [br, bg, bb] = parseHexColor(effect.parameters.background);
      gl.uniform2f(uniformLocation(gl, program, "u_resolution"), this.width, this.height);
      gl.uniform1f(uniformLocation(gl, program, "u_cellSize"), effect.parameters.cellSize);
      gl.uniform1f(uniformLocation(gl, program, "u_angle"), effect.parameters.angle);
      gl.uniform1f(uniformLocation(gl, program, "u_dotGain"), effect.parameters.dotGain);
      gl.uniform1f(uniformLocation(gl, program, "u_threshold"), effect.parameters.threshold);
      gl.uniform1f(uniformLocation(gl, program, "u_invert"), effect.parameters.invert ? 1 : 0);
      gl.uniform3f(uniformLocation(gl, program, "u_foreground"), fr, fg, fb);
      gl.uniform3f(uniformLocation(gl, program, "u_background"), br, bg, bb);
      gl.uniform1f(uniformLocation(gl, program, "u_opacity"), effect.opacity);
    } else if (effect?.definitionId === "noise") {
      gl.uniform2f(uniformLocation(gl, program, "u_resolution"), this.width, this.height);
      gl.uniform1f(uniformLocation(gl, program, "u_amount"), effect.parameters.amount);
      gl.uniform1f(uniformLocation(gl, program, "u_seed"), effect.parameters.seed);
      gl.uniform1f(uniformLocation(gl, program, "u_monochrome"), effect.parameters.monochrome ? 1 : 0);
      gl.uniform1f(uniformLocation(gl, program, "u_opacity"), effect.opacity);
    }

    gl.drawArrays(gl.TRIANGLES, 0, 3);
  }

  dispose() {
    const gl = this.gl;
    Object.values(this.programs).forEach((program) => gl.deleteProgram(program));
    gl.deleteTexture(this.sourceTexture);
    this.pingTextures.forEach((texture) => gl.deleteTexture(texture));
    gl.deleteFramebuffer(this.framebuffer);
    gl.deleteBuffer(this.vertexBuffer);
    gl.deleteVertexArray(this.vao);
    this.renderer.dispose();
  }
}
